import { useEffect, useState } from 'react';
import { AlertTriangle, Wifi, X } from 'lucide-react';
import {
  consumeRevertReason,
  getApiBaseMode,
  isBannerSnoozed,
  probeTailnetReachable,
  setApiBaseMode,
  snoozeBanner,
} from '@lib/apiBase';

/**
 * Auto-detect prompt for tailnet routing.
 *
 * On first load: probe the tailnet API endpoint; if it answers, render
 * a non-intrusive banner offering to switch the user to tailnet routing
 * (which makes streams egress through tk-bakery/NAS instead of Azure).
 *
 * Hidden when:
 *   - user is already on tailnet mode
 *   - banner was snoozed in the last 7 days
 *   - tailnet probe fails (most users)
 *
 * Snooze auto-expires so users who move between tailnet and public
 * networks aren't permanently stuck on whichever mode they happened to
 * be in when they first snoozed.
 */
export default function ConnectionBanner() {
  const [reachable, setReachable] = useState(false);
  const [snoozed, setSnoozed] = useState(isBannerSnoozed());
  // One-shot toast surfaced when the request layer auto-reverted us
  // from tailnet back to public (mobile DoH bypass, etc.). Reads + clears
  // the reason on mount so the toast fires exactly once per revert.
  const [revertReason, setRevertReason] = useState<string | null>(() => consumeRevertReason());

  useEffect(() => {
    if (getApiBaseMode() === 'tailnet') return;
    if (snoozed) return;
    let cancelled = false;
    probeTailnetReachable().then((ok) => { if (!cancelled) setReachable(ok); });
    return () => { cancelled = true; };
  }, [snoozed]);

  if (revertReason) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
          <div className="text-sm text-amber-900 min-w-0">
            <p className="font-medium">Switched back to public</p>
            <p className="text-amber-700 text-xs mt-0.5">{revertReason}</p>
          </div>
        </div>
        <button
          onClick={() => setRevertReason(null)}
          className="p-1.5 text-amber-700 hover:bg-amber-100 rounded-lg"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  if (!reachable || snoozed || getApiBaseMode() === 'tailnet') return null;

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mb-4 flex items-center justify-between gap-3">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <Wifi className="w-5 h-5 text-blue-600 flex-shrink-0" />
        <div className="text-sm text-blue-900 min-w-0">
          <p className="font-medium">You're on the tailnet</p>
          <p className="text-blue-700 text-xs mt-0.5">
            Switch to local routing for faster streaming + no Azure egress costs. Bytes will flow through your home machines.
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={() => setApiBaseMode('tailnet')}
          className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-500"
        >
          Switch
        </button>
        <button
          onClick={() => { snoozeBanner(); setSnoozed(true); }}
          className="p-1.5 text-blue-700 hover:bg-blue-100 rounded-lg"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
